import { useState } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import props from "../../redux/props";
import useToast, { Type } from "../hooks/useToast";
import DragAndDrop from "../UI/DragAndDrop/DragAndDrop";
import { callLambda, s3Upload } from "../../lambda";
import {
  awsPresignedUploadURLLambdaPrefix,
  awsSendMailLambdaPrefix,
  fileMaxFileSizeInBytes,
  fileMimeTypes,
  MAIL_INTENT,
} from "../../utility";

const blue = "rgb(0, 79, 139)";
const white = "rgb(255,255,255)";
const lightgray = "rgb(226,226,226)";

type Application = {
  firstname?: string;
  lastname?: string;
  mail?: string;
  phone?: string;
  entryDate?: string;
  salaryExpectation?: string;
  /** spam trap: hidden field that people leave empty */
  website?: string;
};

type UploadedFile = { key: string; name: string };

const StyledApplyForm = styled.div`
  width: 80%;
  margin: 25px auto;
  font-family: "Open Sans", Helvetica, Arial, sans-serif;
  @media (max-width: 992px) {
    width: 100%;
  }
`;

const Split = styled.form`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const Side = styled.div`
  width: calc(50% - 10px);
  @media (max-width: 992px) {
    width: 100%;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Input = styled.input`
  width: 100%;
  background-color: ${lightgray};
  border: none;
  border-radius: 0px;
  font-size: 16px;
  padding: 7.5px 15px;
  box-sizing: border-box;
  transition: all 0.5s ease;
  color: ${blue};
  ::placeholder {
    color: ${blue};
    opacity: 1;
  }
  &:focus {
    outline: none;
  }
`;

const ButtonWrapper = styled.div`
  text-align: center;
  width: 100%;
`;

const Button = styled.button`
  background-color: ${blue};
  border: none;
  transition: all 0.5s ease;
  padding: 7.5px 50px;
  border-radius: 0px;
  color: ${white};
  cursor: pointer;
  text-transform: uppercase;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
`;

const DropBox = styled.div`
  background-color: ${lightgray};
  width: 100%;
  height: 100%;
  min-height: 100%;
  box-sizing: border-box;
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const DropTitle = styled.div`
  font-weight: bold;
  text-transform: uppercase;
  font-size: 22px;
  color: ${blue};
  text-align: center;
`;

const DropHint = styled.div`
  font-size: 18px;
  color: ${blue};
  text-align: center;
`;

const FileList = styled.div`
  margin-top: 20px;
`;

const FileName = styled.div`
  display: block;
  margin-top: 5px;
  color: ${blue};
  text-align: center;
`;

const ApplyForm: React.FC<{ title: string }> = ({ title }) => {
  const t = useSelector((s) => s[props.TRANSLATION]);
  const toast = useToast();
  const [data, setData] = useState<Application>({});
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const set =
    (key: keyof Application) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setData({ ...data, [key]: e.target.value });

  const onFiles = (dropped: File[]) =>
    setFiles((current) => [...current, ...dropped]);

  // Upload each selected file to S3 via a presigned URL from the upload lambda.
  // Returns the list of { key, name } for the successfully uploaded files.
  const uploadFiles = async (list: File[]): Promise<UploadedFile[]> => {
    const uploaded: UploadedFile[] = [];
    for (const file of list) {
      const response = await callLambda(awsPresignedUploadURLLambdaPrefix, {
        name: file.name,
        type: file.type,
      });
      if (response && response.uploadURL && response.key) {
        const success = await s3Upload(response.uploadURL, file);
        if (success) uploaded.push({ key: response.key, name: file.name });
      }
    }
    return uploaded;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const uploadedFiles = files.length > 0 ? await uploadFiles(files) : [];

      const payload = {
        intent: MAIL_INTENT.CAREER,
        title,
        firstname: data.firstname,
        lastname: data.lastname,
        mail: data.mail,
        phone: data.phone,
        entryDate: data.entryDate,
        salaryExpectation: data.salaryExpectation,
        files: uploadedFiles,
        website: data.website,
      };

      const response = await callLambda(awsSendMailLambdaPrefix, payload);

      if (response && response.status === true) {
        toast(t.career.openPositions.success.form, Type.SUCCESS);
        setData({});
        setFiles([]);
      } else {
        toast(t.career.openPositions.error.form);
      }
    } catch (error) {
      toast(t.career.openPositions.error.form);
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledApplyForm>
      <Split onSubmit={submit}>
        <Side>
            <input
              type="text"
              name="website"
              value={data.website || ""}
              onChange={set("website")}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", overflow: "hidden" }}
            />
          <InputWrapper>
            <Input
              required
              value={data.firstname || ""}
              placeholder={`${t.generic.firstname} *`}
              onChange={set("firstname")}
            />
            <Input
              required
              value={data.lastname || ""}
              placeholder={`${t.generic.lastname} *`}
              onChange={set("lastname")}
            />
            <Input
              required
              type="email"
              value={data.mail || ""}
              placeholder={`${t.generic.mail} *`}
              onChange={set("mail")}
            />
            <Input
              required
              value={data.phone || ""}
              placeholder={`${t.generic.phone} *`}
              onChange={set("phone")}
            />
            <Input
              value={data.entryDate || ""}
              placeholder={t.career.openPositions.form.entryDate}
              onChange={set("entryDate")}
            />
            <Input
              value={data.salaryExpectation || ""}
              placeholder={t.career.openPositions.form.salaryExpectation}
              onChange={set("salaryExpectation")}
            />
          </InputWrapper>
        </Side>
        <Side>
          <DragAndDrop
            onFiles={onFiles}
            allowedMimeTypes={fileMimeTypes}
            maxFileSizeInBytes={fileMaxFileSizeInBytes}
            multiple
          >
            <DropBox>
              <DropTitle>
                {t.career.openPositions.form.dragAndDropTitle}
              </DropTitle>
              {t.career.openPositions.form.dragAndDropHint.map(
                (hint: string, i: number) => (
                  <DropHint key={i}>{hint}</DropHint>
                )
              )}
              {files.length > 0 && (
                <FileList>
                  {files.map((f, i) => (
                    <FileName key={i}>{f.name}</FileName>
                  ))}
                </FileList>
              )}
            </DropBox>
          </DragAndDrop>
        </Side>
        <ButtonWrapper>
          <Button type="submit" disabled={loading}>
            {t.career.openPositions.form.cta}
          </Button>
        </ButtonWrapper>
      </Split>
    </StyledApplyForm>
  );
};

export default ApplyForm;
